import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TrendUp, Users, Clock, XCircle, Percent, PaperPlaneTilt } from "@phosphor-icons/react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";

const STATUS_STYLE = {
  active: "bg-emerald-600 text-white",
  trialing: "bg-amber-500 text-black",
  expired: "bg-secondary text-muted-foreground",
};

export default function AdminSubscriptions() {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);

  const load = () => api.get("/admin/subscriptions").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const emailTrials = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post("/admin/notify-trials-ending");
      toast.success(res.sent ? `Reminder sent to ${res.sent} trial team${res.sent === 1 ? "" : "s"}` : "No trials ending within 7 days");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  if (data === null) return <div className="p-8 label-caps animate-pulse">Loading…</div>;
  if (data === false) return <div className="p-8 text-sm text-muted-foreground">Admin access only.</div>;

  const s = data.summary;
  const cards = [
    { label: "Est. MRR", value: money(s.mrr), icon: TrendUp, testid: "admin-mrr" },
    { label: "Conversion", value: `${s.conversion_rate}%`, icon: Percent, testid: "admin-conversion" },
    { label: "Active (paid)", value: s.active, icon: Users, testid: "admin-active" },
    { label: "Trialing", value: s.trialing, icon: Clock, testid: "admin-trialing" },
    { label: "Expired", value: s.expired, icon: XCircle, testid: "admin-expired" },
  ];

  return (
    <div>
      <PageHeader label="Admin" title="Subscriptions" />
      <div className="p-4 sm:p-8">
        <div className="grid grid-cols-2 lg:grid-cols-5 border-t border-l border-border mb-8 stagger" data-testid="admin-summary">
          {cards.map((c) => (
            <div key={c.label} data-testid={c.testid} className="border-r border-b border-border p-6 min-h-[120px] flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <p className="label-caps">{c.label}</p>
                <c.icon size={18} className="text-muted-foreground" />
              </div>
              <div className="font-mono-fig text-3xl font-bold tabular-nums">{c.value}</div>
            </div>
          ))}
        </div>

        <div className="border border-border mb-8 reveal" data-testid="admin-signups-chart">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <span className="font-heading font-bold">New signups — last 30 days</span>
            <span className="label-caps">{data.summary.converted} converted</span>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={data.signups} margin={{ left: 0, right: 10, top: 8 }}>
                <defs>
                  <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0A0A0A" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#0A0A0A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={4} stroke="#a1a1aa" />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} stroke="#a1a1aa" width={28} />
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #0A0A0A", fontSize: 12 }} />
                <Area type="monotone" dataKey="count" stroke="#0A0A0A" strokeWidth={2} fill="url(#sg)" name="Signups" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {data.ending_soon.length > 0 && (
          <div className="border border-amber-500 mb-8 reveal" data-testid="admin-ending-soon">
            <div className="px-4 py-3 border-b border-amber-500 bg-amber-500 text-black flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2"><Clock size={16} weight="bold" /> <span className="font-heading font-bold">Trials ending this week ({data.ending_soon.length})</span></div>
              <Button onClick={emailTrials} disabled={sending} size="sm" className="rounded-none bg-black text-white hover:bg-black/90 h-8" data-testid="email-trials-button">
                <PaperPlaneTilt size={14} className="mr-1" /> {sending ? "Sending…" : "Email all"}
              </Button>
            </div>
            <div className="divide-y divide-border">
              {data.ending_soon.map((t, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-3 text-sm">
                  <div><span className="font-medium">{t.team_name}</span> <span className="text-muted-foreground">· {t.email}</span></div>
                  <span className="font-mono-fig text-amber-700 font-semibold">{t.days_left}d left</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="border border-border reveal">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Team</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Valid until</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.teams.map((t, i) => (
                <TableRow key={i} data-testid={`admin-team-row-${i}`} className="hover:bg-secondary transition-colors duration-200">
                  <TableCell className="font-medium">{t.team_name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{t.email}</TableCell>
                  <TableCell className="text-sm capitalize">{t.plan}</TableCell>
                  <TableCell>
                    <span className={`inline-block px-2 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLE[t.status]}`}>
                      {t.status}{t.status === "trialing" ? ` · ${t.days_left}d` : ""}
                    </span>
                  </TableCell>
                  <TableCell className="text-right text-sm font-mono-fig text-muted-foreground">
                    {t.until ? new Date(t.until).toLocaleDateString() : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
